import { StateRowTransportCollection, StateRowTransportValue } from '../../../backend.d'
import { ProfileId } from '../ProfileId.type'
import randomStateIdValue from '../state/randomStateIdValue'
import { State } from '../state/State.type'
import { encodePersistedStateBytes } from '../state/persisted_state'
import { StateOnlineId } from '../state/StateOnlineId.type'

class MemoryDiskStore {
  private rows = new Map<string, StateRowTransportValue>()

  get(key: string): StateRowTransportValue | null {
    return this.rows.get(key) || null
  }

  set(key: string, value: StateRowTransportValue): void {
    this.rows.set(key, value)
  }

  delete(key: string): void {
    this.rows.delete(key)
  }

  keys(): string[] {
    return [...this.rows.keys()]
  }

  reset(): void {
    this.rows.clear()
  }
}

export class MemoryDisk {
  private readonly prefix: string
  private readonly store = new MemoryDiskStore()

  constructor(type: string) {
    this.prefix = `st1_memory_${type}_`
  }

  async save(state: State): Promise<StateRowTransportValue> {
    let id = state.map.id
    if (!id) {
      id = this.generateUniqueId()
      state.map.id = id as StateOnlineId
    }

    const now = new Date().toISOString()
    const user = { id: 0 as ProfileId, handle: 'Local', dname: 'Local Player', avatar_id: null }
    const existingRow = this.store.get(`${this.prefix}${id}`)

    const row: StateRowTransportValue = {
      id: id as StateOnlineId,
      data: encodePersistedStateBytes(state),
      action_logs: existingRow?.action_logs || [],
      created_at: existingRow?.created_at || now,
      updated_at: now,
      published_at: existingRow?.published_at || null,
      deleted_at: null,
      creator: existingRow?.creator || user,
      owner: existingRow?.owner || user,
      turn: existingRow?.turn || null,
    }

    this.store.set(`${this.prefix}${id}`, row)
    return row
  }

  async load(id: number | string): Promise<StateRowTransportValue | null> {
    return this.store.get(`${this.prefix}${id}`)
  }

  async list(): Promise<StateRowTransportCollection> {
    return this.store.keys()
      .filter((key) => key.startsWith(this.prefix))
      .map((key) => this.store.get(key))
      .filter((row): row is StateRowTransportValue => row !== null)
  }

  async delete(id: number | string): Promise<void> {
    this.store.delete(`${this.prefix}${id}`)
  }

  reset(): void {
    this.store.reset()
  }

  private generateUniqueId(): number {
    const maxAttempts = 100
    for (let i = 0; i < maxAttempts; i++) {
      const id = randomStateIdValue()
      if (!this.store.get(`${this.prefix}${id}`)) {
        return id
      }
    }
    throw new Error('Failed to generate unique ID after multiple attempts')
  }
}

export const memoryMapDisk = new MemoryDisk('map')
export const memoryGameDisk = new MemoryDisk('game')
export const memoryLobbyDisk = new MemoryDisk('lobby')
export const memoryReplayDisk = new MemoryDisk('replay')

export function resetAllMemoryDisks(): void {
  memoryMapDisk.reset()
  memoryGameDisk.reset()
  memoryLobbyDisk.reset()
  memoryReplayDisk.reset()
}
